import { useState, useEffect } from 'react';
import {
  CreditCard,
  Plus,
  Trash2,
  Edit2,
  DollarSign,
  Banknote,
  Star,
  ArrowUp,
  ArrowDown,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { usePaymentTypes } from '@/hooks/usePaymentTypes';
import { useLocations } from '@/hooks/useLocations';
import { useLocationStore } from '@/store/location';

interface Toast {
  type: 'success' | 'error';
  message: string;
}

interface PaymentTypesManagerProps {
  showHeader?: boolean;
}

export function PaymentTypesManager({ showHeader = true }: PaymentTypesManagerProps) {
  const [toast, setToast] = useState<Toast | null>(null);

  // Location settings
  const { currentLocation } = useLocationStore();
  const { updateLocationSettings } = useLocations();
  const hideDefaultPaymentTypes = currentLocation?.settings?.hide_default_payment_types ?? false;

  // Payment types
  const {
    paymentTypes,
    isLoading: paymentTypesLoading,
    createPaymentType,
    updatePaymentType,
    deletePaymentType,
    setPrimaryPaymentType,
    updateSortOrders,
  } = usePaymentTypes();

  const [paymentTypeDialogOpen, setPaymentTypeDialogOpen] = useState(false);
  const [editingPaymentType, setEditingPaymentType] = useState<typeof paymentTypes[0] | null>(null);
  const [paymentTypeForm, setPaymentTypeForm] = useState({
    name: '',
    code: '',
    is_cash_equivalent: false,
  });
  const [savingPaymentType, setSavingPaymentType] = useState(false);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
  };

  const movePaymentType = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= paymentTypes.length) return;

    const reordered = [...paymentTypes];
    const [removed] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, removed);

    updateSortOrders.mutate(reordered.map(pt => pt.id));
  };

  return (
    <div className="space-y-6">
      {/* Toast notification */}
      {toast && (
        <div
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium animate-in slide-in-from-top-2',
            toast.type === 'success' && 'bg-green-500/10 text-green-600 border border-green-500/20',
            toast.type === 'error' && 'bg-red-500/10 text-red-600 border border-red-500/20'
          )}
        >
          {toast.type === 'success' ? (
            <Check className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          {toast.message}
        </div>
      )}

      <div className="flex items-center justify-between">
        {showHeader && (
          <div>
            <h3 className="text-lg font-semibold">Payment Types</h3>
            <p className="text-sm text-muted-foreground">
              Configure custom payment methods. The primary payment type will be highlighted orange on the POS.
            </p>
          </div>
        )}
        <Button
          variant="blaze"
          className="gap-2"
          onClick={() => {
            setEditingPaymentType(null);
            setPaymentTypeForm({ name: '', code: '', is_cash_equivalent: false });
            setPaymentTypeDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Add Payment Type
        </Button>
      </div>

      {/* Default Payment Types Toggle */}
      <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-muted">
            <DollarSign className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium">Hide Default Payment Types</p>
            <p className="text-sm text-muted-foreground">
              Turn off the default Cash &amp; Card buttons when you have custom payment types
            </p>
          </div>
        </div>
        <Switch
          checked={hideDefaultPaymentTypes}
          onCheckedChange={(checked) => {
            if (currentLocation?.id) {
              updateLocationSettings.mutate({
                id: currentLocation.id,
                settings: { hide_default_payment_types: checked },
              });
            }
          }}
          disabled={updateLocationSettings.isPending}
        />
      </div>

      {paymentTypesLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : paymentTypes.length === 0 ? (
        <div className="text-center py-12 border rounded-xl bg-muted/30">
          <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h4 className="font-medium">No Payment Types</h4>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Add custom payment methods for your business
          </p>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              setEditingPaymentType(null);
              setPaymentTypeForm({ name: '', code: '', is_cash_equivalent: false });
              setPaymentTypeDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add First Payment Type
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {paymentTypes.map((pt, index) => (
            <div
              key={pt.id}
              className={cn(
                "flex items-center justify-between p-4 rounded-xl border bg-card transition-all",
                pt.is_primary && "ring-2 ring-orange-500/50 border-orange-500/30 bg-orange-500/5"
              )}
            >
              <div className="flex items-center gap-3">
                {/* Reorder Controls */}
                <div className="flex flex-col gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    disabled={index === 0 || updateSortOrders.isPending}
                    onClick={() => movePaymentType(index, 'up')}
                  >
                    <ArrowUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    disabled={index === paymentTypes.length - 1 || updateSortOrders.isPending}
                    onClick={() => movePaymentType(index, 'down')}
                  >
                    <ArrowDown className="h-3 w-3" />
                  </Button>
                </div>

                <div className={cn(
                  "p-2 rounded-lg",
                  pt.is_primary ? "bg-orange-500/10" : pt.is_cash_equivalent ? "bg-green-500/10" : "bg-blue-500/10"
                )}>
                  {pt.is_cash_equivalent ? (
                    <Banknote className={cn("h-5 w-5", pt.is_primary ? "text-orange-600" : "text-green-600")} />
                  ) : (
                    <CreditCard className={cn("h-5 w-5", pt.is_primary ? "text-orange-600" : "text-blue-600")} />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{pt.name}</p>
                    {pt.is_primary && (
                      <Badge className="bg-orange-500 hover:bg-orange-600 text-xs">
                        Primary
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Code: {pt.code}
                    {pt.is_cash_equivalent && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        Cash Equivalent
                      </Badge>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Set Primary Button */}
                <Button
                  variant={pt.is_primary ? "default" : "ghost"}
                  size="icon"
                  className={cn(
                    pt.is_primary && "bg-orange-500 hover:bg-orange-600"
                  )}
                  onClick={() => {
                    if (!pt.is_primary) {
                      setPrimaryPaymentType.mutate(pt.id);
                    }
                  }}
                  disabled={setPrimaryPaymentType.isPending}
                  title={pt.is_primary ? "Primary payment type" : "Set as primary"}
                >
                  <Star className={cn("h-4 w-4", pt.is_primary && "fill-current")} />
                </Button>
                <Switch
                  checked={pt.is_active ?? false}
                  onCheckedChange={(checked) => {
                    updatePaymentType.mutate({
                      id: pt.id,
                      updates: { is_active: checked },
                    });
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditingPaymentType(pt);
                    setPaymentTypeForm({
                      name: pt.name,
                      code: pt.code,
                      is_cash_equivalent: pt.is_cash_equivalent ?? false,
                    });
                    setPaymentTypeDialogOpen(true);
                  }}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    if (confirm(`Delete "${pt.name}"?`)) {
                      deletePaymentType.mutate(pt.id);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      <div className="p-4 rounded-xl border bg-muted/30">
        <h4 className="font-medium mb-2 flex items-center gap-2">
          <Star className="h-4 w-4 text-orange-500" />
          Button Order & Primary
        </h4>
        <p className="text-sm text-muted-foreground">
          Use the arrows to reorder payment buttons on the POS screen.
          Click the star to set a payment type as primary — it will appear as the highlighted orange button.
          Payment buttons appear on the POS in the order shown here.
        </p>
      </div>

      {/* Payment Type Dialog */}
      <ResponsiveModal open={paymentTypeDialogOpen} onOpenChange={setPaymentTypeDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingPaymentType ? 'Edit Payment Type' : 'Add Payment Type'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {editingPaymentType
                ? 'Update the payment type details'
                : 'Create a custom payment method for your POS'}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="paymentTypeName">Name *</Label>
                <Input
                  id="paymentTypeName"
                  value={paymentTypeForm.name}
                  onChange={(e) => setPaymentTypeForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., EPAY, Rubis Subsidy"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentTypeCode">Code *</Label>
                <Input
                  id="paymentTypeCode"
                  value={paymentTypeForm.code}
                  onChange={(e) => setPaymentTypeForm(prev => ({
                    ...prev,
                    code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                  }))}
                  placeholder="e.g., epay, rubis_subsidy"
                />
                <p className="text-xs text-muted-foreground">
                  Lowercase letters, numbers, and underscores only
                </p>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <Label htmlFor="isCashEquivalent" className="font-medium">Cash Equivalent</Label>
                  <p className="text-sm text-muted-foreground">
                    Counts towards cash drawer balance
                  </p>
                </div>
                <Switch
                  id="isCashEquivalent"
                  checked={paymentTypeForm.is_cash_equivalent}
                  onCheckedChange={(checked) => setPaymentTypeForm(prev => ({
                    ...prev,
                    is_cash_equivalent: checked
                  }))}
                />
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button
              variant="outline"
              onClick={() => setPaymentTypeDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="blaze"
              disabled={!paymentTypeForm.name || !paymentTypeForm.code || savingPaymentType}
              onClick={async () => {
                setSavingPaymentType(true);
                try {
                  if (editingPaymentType) {
                    await updatePaymentType.mutateAsync({
                      id: editingPaymentType.id,
                      updates: {
                        name: paymentTypeForm.name,
                        code: paymentTypeForm.code,
                        is_cash_equivalent: paymentTypeForm.is_cash_equivalent,
                      },
                    });
                    showToast('success', 'Payment type updated');
                  } else {
                    await createPaymentType.mutateAsync({
                      name: paymentTypeForm.name,
                      code: paymentTypeForm.code,
                      is_cash_equivalent: paymentTypeForm.is_cash_equivalent,
                    });
                    showToast('success', 'Payment type created');
                  }
                  setPaymentTypeDialogOpen(false);
                } catch (error: any) {
                  showToast('error', error.message || 'Failed to save payment type');
                } finally {
                  setSavingPaymentType(false);
                }
              }}
            >
              {savingPaymentType && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingPaymentType ? 'Update' : 'Create'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
